import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import CustomDatePicker from 'components/CustomDatePicker';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  Chip,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import StyledButton from 'components/StyledButton';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import ErrorBoundary from 'components/ErrorBoundary';
import SessionAttendance from './SessionAttendance';


const STATUS_OPTIONS = ['planned', 'active', 'completed', 'cancelled'];

const parseTimeString = (s) => {
  if (!s) return null;
  const [h, m] = s.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

const formatTimeValue = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return `${v.getHours().toString().padStart(2, '0')}:${v.getMinutes().toString().padStart(2, '0')}`;
};

const TrainingSessionForm = () => {
  useAuthCheck();
  const { trainingSessionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { startDate, endDate } = useDateRangeContext();

  const isNew = !trainingSessionId || trainingSessionId === 'new';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [meta, setMeta] = useState({ createdBy: null, updatedBy: null, createdAt: null, updatedAt: null });

  const [form, setForm] = useState({
    subjects: [],
    trainingCourseId: '',
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    location: '',
    maxCapacity: 20,
    status: 'planned',
    notes: '',
    enrollments: [],
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        subjectsRes, 
        employeesRes, 
        coursesRes,
      ] = await Promise.all([
        api.get('/trainingsubjects/active'),
        api.get('/employees/active'),
        api.post('/trainingcourses', { startDate, endDate }),
      ]);

      setSubjects(subjectsRes.data || []);
      setEmployeeList(employeesRes.data || []);
      setCourses(coursesRes.data || []);

      if (!isNew) {
        const sessionRes = await api.get(`/trainingsessions/${trainingSessionId}`);
        const d = sessionRes.data;
        setForm({
          subjects: d.subjects || [],
          trainingCourseId: d.trainingCourseId?._id || d.trainingCourseId || '',
          startDate: d.startDate ? new Date(d.startDate) : null,
          endDate: d.endDate ? new Date(d.endDate) : null,
          startTime: d.startTime ? parseTimeString(d.startTime) : null,
          endTime: d.endTime ? parseTimeString(d.endTime) : null,
          location: d.location || '',
          maxCapacity: d.maxCapacity || 20,
          status: d.status || 'planned',
          notes: d.notes || '',
          enrollments: d.enrollments || [],
        });
        setMeta({
          createdBy: d.createdBy,
          updatedBy: d.updatedBy,
          createdAt: d.createdAt,
          updatedAt: d.updatedAt,
        });
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [
    startDate, 
    endDate, 
    isNew, 
    trainingSessionId,
  ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCourseChange = async (e) => {
    const id = e.target.value;
    const course = courses.find((c) => c._id === id);
    if (!course) {
      setForm((prev) => ({ ...prev, trainingCourseId: id }));
      return;
    }

    let isFirstSession = true;
    try {
      const res = await api.get(`/trainingsessions?trainingCourseId=${id}&limit=1`);
      const existing = res.data?.data || res.data || [];
      isFirstSession = existing.length === 0;
    } catch { /* keep isFirstSession = true on error */ }

    const dateTimeDefaults = {
      startDate: course.startDate ? new Date(course.startDate) : null,
      endDate:   course.endDate   ? new Date(course.endDate)   : null,
      startTime: course.startTime ? parseTimeString(course.startTime) : null,
      endTime:   course.endTime   ? parseTimeString(course.endTime)   : null,
      maxCapacity: course.maxCapacity ?? 20,
    };

    if (!isFirstSession) {
      setForm((prev) => ({ ...prev, trainingCourseId: id, ...dateTimeDefaults }));
      return;
    }

    const courseSubjects = course.subjects || [];
    const resolvedSubjects = courseSubjects.length === 1
      ? subjects.filter((s) => s._id === (courseSubjects[0]?._id || courseSubjects[0]))
      : [];

    setForm((prev) => ({
      ...prev,
      trainingCourseId: id,
      ...dateTimeDefaults,
      subjects: resolvedSubjects,
      location: course.location || '',
      notes: course.notes || '',
    }));
  };

  const handleDateChange = (name, val) => setForm((prev) => ({ ...prev, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.startDate) {
      toast.error('Start date is required.');
      return;
    }
    try {
      setIsSubmitting(true);
      const payload = {
        ...form,
        subjects: form.subjects.map((s) => s._id || s),
        startTime: formatTimeValue(form.startTime),
        endTime: formatTimeValue(form.endTime),
        enrollments: form.enrollments.map((e) => ({
          ...e,
          results: (e.results || []).map((r) => ({ ...r, subjectId: r.subjectId?._id || r.subjectId })),
        })),
      };
      if (isNew) {
        await api.post('/trainingsessions/new', payload);
        toast.success('Training session created.');
      } else {
        await api.put(`/trainingsessions/update/${trainingSessionId}`, payload);
        toast.success('Training session updated.');
      }
      navigate('/trainingsessions');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save training session!');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress size={28} /></FlexCenter>;
  if (error) return <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">{error.message}</Typography></FlexCenter>;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ErrorBoundary>
        <Box className="main-container-box">
          <FlexBetween>
            <Header
              title='Training Session'
              subtitle={`${loading ? 'Loading...' : !isNew ? 'Update' : 'Create'}`}
            />
            <StyledButton onClick={() => navigate('/trainingsessions')}>
              <ArrowBackIosNew />
            </StyledButton>
          </FlexBetween>

          <Box 
            height={LAYOUT_HEIGHT} 
            component="form" 
            onSubmit={handleSubmit}           
            sx={{ overflowY: 'auto' }}
          >
            <Box
              display="grid"
              gridTemplateColumns="repeat(12, minmax(0, 1fr))"
              gap={2}
              py={1}
            >
              <TextField
                select
                required
                size="small"
                label="Training Course"
                value={form.trainingCourseId}
                onChange={handleCourseChange}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ gridColumn: isDesktop ? 'span 4' : 'span 12' }}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.courseName}</MenuItem>
                ))}
              </TextField>

              <Autocomplete
                multiple
                size="small"
                options={subjects}
                getOptionLabel={(o) =>
                  typeof o === 'string' ? o : `${o.subjectCode} - ${o.subjectName}`
                }
                value={form.subjects}
                onChange={(_, v) => setForm((prev) => ({ ...prev, subjects: v }))}
                isOptionEqualToValue={(o, v) => (o._id || o) === (v._id || v)}
                renderValue={(selected, getItemProps) =>
                  selected.map((opt, idx) => (
                    <Chip
                      key={opt._id || idx}
                      label={opt.subjectCode || opt}
                      size="small"
                      {...getItemProps(idx)}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subjects"
                    placeholder={form.subjects.length === 0 ? 'Select subjects…' : ''}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
                sx={{ gridColumn: isDesktop ? 'span 8' : 'span 12' }}
              />

              <CustomDatePicker
                label="Start Date"
                name="startDate"
                value={form.startDate}
                onChange={handleDateChange}
                size="small"
                variant="outlined"
                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
              />

              <CustomDatePicker
                label="End Date"
                name="endDate"
                value={form.endDate}
                onChange={handleDateChange}
                size="small"
                variant="outlined"
                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
              />

              <TimePicker
                label="Start Time"
                value={form.startTime}
                onChange={(val) => setForm((prev) => ({ ...prev, startTime: val }))}
                ampm={false}
                slotProps={{ textField: { size: 'small', variant: 'outlined', fullWidth: true } }}
                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
              />

              <TimePicker
                label="End Time"
                value={form.endTime}
                onChange={(val) => setForm((prev) => ({ ...prev, endTime: val }))}
                ampm={false}
                slotProps={{ textField: { size: 'small', variant: 'outlined', fullWidth: true } }}
                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
              />

              <TextField
                size="small"
                label="Location"
                value={form.location}
                onChange={handleChange('location')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
              />

              <TextField
                size="small"
                type="number"
                label="Max Capacity"
                value={form.maxCapacity}
                onChange={handleChange('maxCapacity')}
                slotProps={{ inputProps: { min: 1, max: 200 }, inputLabel: { shrink: true } }}
                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
              />

              <TextField
                select
                size="small"
                label="Status"
                value={form.status}
                onChange={handleChange('status')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                multiline
                minRows={1}
                label="Notes"
                value={form.notes}
                onChange={handleChange('notes')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ gridColumn: 'span 12' }}
              />

              {/* Attendance & Results */}
              <SessionAttendance
                sessionId={trainingSessionId}
                isNew={isNew}
                enrollments={form.enrollments}
                subjects={form.subjects}
                subjectsData={subjects}
                employeeList={employeeList}
                maxCapacity={form.maxCapacity}
                trainingCourseId={form.trainingCourseId}
                onEnrollmentsChange={(enrollments) => setForm((prev) => ({ ...prev, enrollments }))}
              />

              {!isNew && (meta.createdBy || meta.updatedBy) && (
                <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {meta.createdBy && (
                    <Typography variant="caption" color="text.secondary">
                      Created by: {meta.createdBy.firstName} {meta.createdBy.lastName}
                      {meta.createdAt ? ` · ${format(new Date(meta.createdAt), 'dd/MM/yyyy HH:mm')}` : ''}
                    </Typography>
                  )}
                  {meta.updatedBy && (
                    <Typography variant="caption" color="text.secondary">
                      Updated by: {meta.updatedBy.firstName} {meta.updatedBy.lastName}
                      {meta.updatedAt ? ` · ${format(new Date(meta.updatedAt), 'dd/MM/yyyy HH:mm')}` : ''}
                    </Typography>
                  )}
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ gridColumn: '1 / -1', height: 42 }}
              >
                {isSubmitting ? 'Saving…' : isNew ? 'Create' : 'Update'}
              </Button>
            </Box>
          </Box>
        </Box>
      </ErrorBoundary>
    </LocalizationProvider>
  );
};

export default TrainingSessionForm;
